import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import CmsSidebar from "./components/layout/CmsSidebar";
import CmsTopBar from "./components/layout/CmsTopBar";
import CmsBreadcrumbs from "./components/layout/CmsBreadcrumbs";
import CmsCommandPalette from "./components/layout/CmsCommandPalette";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./contexts/AuthContext";
import { CmsPanelProvider, useCmsPanel } from "./contexts/CmsPanelContext";
import { cn } from "./lib/utils";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// CMS pages
import HomePage from "./features/cms/pages/HomePage";
import EventDetailPage from "./features/cms/pages/EventDetailPage";
import EventRegistrationPage from "./features/cms/pages/EventRegistrationPage";
import RaceContestantsPage from "./features/cms/pages/RaceContestantsPage";
import OrganizersPage from "./features/cms/pages/OrganizersPage";
import OrganizerDetailPage from "./features/cms/pages/OrganizerDetailPage";
import PostsPage from "./features/cms/pages/PostsPage";
import CMSDashboard from "./features/cms/pages/CMSDashboard";

// CMS route pages
import CmsEventsPage from "./features/cms/pages/CmsEventsPage";
import CmsRegistrationsPage from "./features/cms/pages/CmsRegistrationsPage";
import CmsSeriesPage from "./features/cms/pages/CmsSeriesPage";
import CmsOrganizersPage from "./features/cms/pages/CmsOrganizersPage";
import CmsPostsPage from "./features/cms/pages/CmsPostsPage";
import CmsImportRegistrationsPage from "./features/cms/pages/CmsImportRegistrationsPage";
import CmsAiPage from "./features/cms/pages/CmsAiPage";

// Auth pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";

// Public pages
import Home from "./pages/public/home";
import PublicHomePage from "./pages/public/PublicHomePage";
import PrivacyPolicyPage from "./pages/public/PrivacyPolicy/privacypolicy.view";
import RaceInfoPage from "./pages/public/RaceInfo/raceinfo.view";
import RaceResultsPage from "./pages/public/RaceResultsPage";
import ResultsListPage from "./pages/public/ResultsListPage";

// Feedback pages
import RegistrationSuccessPage from "./pages/feedback/RegistrationSuccessPage";
import RegistrationFailPage from "./pages/feedback/RegistrationFailPage";

// Layout wrapper for public CMS pages (event detail, registration, etc.)
function PublicCmsLayout() {
  return (
    <ProtectedRoute>
      <Navbar />
      <Outlet />
      <Footer />
    </ProtectedRoute>
  );
}

// Layout wrapper for CMS admin with sidebar
function CmsLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('cms-sidebar-collapsed') === 'true';
  });
  const sidebarWasExpandedRef = React.useRef(false);
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem('cms-sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  return (
    <ProtectedRoute>
      <CmsPanelProvider>
        <CmsLayoutInner
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          cmdOpen={cmdOpen}
          setCmdOpen={setCmdOpen}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          sidebarWasExpandedRef={sidebarWasExpandedRef}
          location={location}
        />
      </CmsPanelProvider>
    </ProtectedRoute>
  );
}

function CmsLayoutInner({
  mobileOpen, setMobileOpen, cmdOpen, setCmdOpen,
  sidebarCollapsed, setSidebarCollapsed, sidebarWasExpandedRef, location,
}: {
  mobileOpen: boolean;
  setMobileOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cmdOpen: boolean;
  setCmdOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarWasExpandedRef: React.MutableRefObject<boolean>;
  location: ReturnType<typeof useLocation>;
}) {
  const { isPanelOpen, setPanelContainer, closePanel } = useCmsPanel();

  // Auto-close panel on route change
  useEffect(() => {
    closePanel();
  }, [location.pathname, closePanel]);

  // Sync sidebar with panel: collapse on open, restore on close
  useEffect(() => {
    if (isPanelOpen) {
      setMobileOpen(false);
      if (!sidebarCollapsed) {
        sidebarWasExpandedRef.current = true;
        setSidebarCollapsed(true);
      }
    } else {
      if (sidebarWasExpandedRef.current) {
        sidebarWasExpandedRef.current = false;
        setSidebarCollapsed(false);
      }
    }
  }, [isPanelOpen, sidebarCollapsed, setSidebarCollapsed, setMobileOpen, sidebarWasExpandedRef]);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <CmsSidebar
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
      />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <CmsTopBar onMenuClick={() => setMobileOpen((o) => !o)} onSearchClick={() => setCmdOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 lg:px-8 pt-4">
            <CmsBreadcrumbs />
          </div>
          <div key={location.pathname} className="animate-in fade-in-0 duration-300">
            <Outlet />
          </div>
        </main>
      </div>
      {/* Push panel — part of flex flow, pushes content left */}
      <div
        ref={setPanelContainer}
        className={cn(
          'shrink-0 border-l border-border bg-background overflow-y-auto overflow-x-hidden transition-all duration-300 ease-in-out',
          isPanelOpen ? 'w-full md:w-[420px]' : 'w-0 border-l-0'
        )}
      />
      <CmsCommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public Website Routes - use their own layout via PageTemplate */}
            <Route path="/" element={<Home />} />
            <Route path="/public" element={<PublicHomePage />} />
            <Route path="/registration-success" element={<RegistrationSuccessPage />} />
            <Route path="/success-registration" element={<RegistrationSuccessPage />} />
            <Route path="/fail-registration" element={<RegistrationFailPage />} />
            <Route path="/registration-fail" element={<RegistrationFailPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/race-info" element={<RaceInfoPage />} />
            <Route path="/results" element={<ResultsListPage />} />
            <Route path="/results/:id" element={<RaceResultsPage />} />

            {/* Auth Routes (public, but redirect to /cms if already logged in) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* CMS Admin Routes - sidebar layout */}
            <Route element={<CmsLayout />}>
              <Route path="/cms" element={<CMSDashboard />} />
              <Route path="/cms/events" element={<CmsEventsPage />} />
              <Route path="/cms/registrations" element={<CmsRegistrationsPage />} />
              <Route path="/cms/registrations/import" element={<CmsImportRegistrationsPage />} />
              <Route path="/cms/series" element={<CmsSeriesPage />} />
              <Route path="/cms/organizers" element={<CmsOrganizersPage />} />
              <Route path="/cms/posts" element={<CmsPostsPage />} />
              <Route path="/cms/ai" element={<CmsAiPage />} />
            </Route>

            {/* Protected pages with Navbar layout (event detail, registration, etc.) */}
            <Route element={<PublicCmsLayout />}>
              <Route path="/events" element={<HomePage />} />
              <Route path="/events/:id" element={<EventDetailPage />} />
              <Route path="/events/:id/register" element={<EventRegistrationPage />} />
              <Route path="/events/:id/contestants" element={<RaceContestantsPage />} />
              <Route path="/organizers" element={<OrganizersPage />} />
              <Route path="/organizers/:id" element={<OrganizerDetailPage />} />
              <Route path="/posts" element={<PostsPage />} />
            </Route>

            {/* Catch-all: redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
    {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
